import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, filter, takeUntil } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { Client } from '@microsoft/microsoft-graph-client';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, AuthenticationResult, InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser'
import { datadogRum } from '@datadog/browser-rum';


@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {  
  private authState = new BehaviorSubject<boolean>(this.hasValidToken() && Number(sessionStorage.getItem('appSessionExpiresAt')) > Date.now());
  private sessionReady = !!sessionStorage.getItem('appSessionExpiresAt') && !!localStorage.getItem('authToken');
  private sessionGeneration = 0;
  private readonly msalReady: Promise<void>;
  private msalInitialized = false;
  private validation?: Promise<void>;
  private watcher: any;
  private readonly resume = () => { if (document.visibilityState !== 'hidden') void this.checkSession(); };
  private logoutTimer: any;
  private readonly MAX_SESSION_MS = 8 * 60 * 60 * 1000; // 8 hours
  private userImageSource = new BehaviorSubject<string>(localStorage.getItem('image') || '');
  private userNameSource = new BehaviorSubject<string>(localStorage.getItem('displayName') || '');
  private userEmailSource = new BehaviorSubject<string>(localStorage.getItem('email') || '');
  private destroy$ = new Subject<void>();

  image$ = this.userImageSource.asObservable();
  fullName$ = this.userNameSource.asObservable();
  email$ = this.userEmailSource.asObservable();
  

  constructor(
    private msal: MsalService,
    private msalBroadcast: MsalBroadcastService
  ) {
    // Login and API requests share the root MSAL client and its initialization.
    this.msalReady = this.msal.instance.initialize().then(() => {
      this.msalInitialized = true;
      this.syncAuthStateFromMsal();
    });
    void this.msalReady.catch(() => { /* Token requests report initialization errors. */ });
    this.scheduleHardLogout();
    this.watcher = setInterval(() => void this.checkSession(), 60000);
    window.addEventListener('focus', this.resume);
    document.addEventListener('visibilitychange', this.resume);
    void this.checkSession();

    // 2) update state when MSAL finishes interactions
    this.msalBroadcast.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.syncAuthStateFromMsal();
        if (this.sessionReady && !this.authState.value) void this.checkSession();
      });

    // 3) update state on key auth/token events
    this.msalBroadcast.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.LOGIN_SUCCESS ||
          msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          msg.eventType === EventType.SSO_SILENT_SUCCESS ||
          msg.eventType === EventType.LOGOUT_SUCCESS
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((msg: EventMessage) => {
        if (
          msg.eventType === EventType.LOGIN_SUCCESS ||
          msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          msg.eventType === EventType.SSO_SILENT_SUCCESS
        ) {
          const result = msg.payload as AuthenticationResult;
          if (result?.account) {
            this.msal.instance.setActiveAccount(result.account);
          }
        }

        this.syncAuthStateFromMsal();
      });
  }

  private syncAuthStateFromMsal(): void {
    if (!this.msalInitialized) return;
    const active = this.msal.instance.getActiveAccount();
    const accounts = this.msal.instance.getAllAccounts();
    // An account in the MSAL cache does not prove the app session is valid.
    if (!active && accounts.length > 0) this.msal.instance.setActiveAccount(accounts[0]);
    if (this.isAppSessionExpired()) { this.logout(); return; }
    if (this.sessionReady && !active && !accounts.length) { this.logout(); return; }

  }

  private async checkSession(): Promise<void> {
    if (!this.sessionReady || !sessionStorage.getItem('appSessionExpiresAt')) return;
    if (this.isAppSessionExpired()) { this.logout(); return; }
    if (this.validation) return this.validation;
    this.validation = Promise.resolve().then(() => this.validateSession()).finally(() => { this.validation = undefined; });
    return this.validation;
  }

  private async validateSession(): Promise<void> {
    try {
      await this.acquireSessionToken(['User.Read', 'GroupMember.Read.All'], 'authToken');
      await this.acquireSessionToken(['https://database.windows.net/.default'], 'ms_authToken');
      if (this.sessionReady && !this.isAppSessionExpired()) this.authState.next(true);
    } catch { /* Retry network failures on the next check; auth failures clear the session. */ }
  }

  async getSqlAccessToken(): Promise<string> {
    return this.acquireSessionToken(['https://database.windows.net/.default'], 'ms_authToken');
  }

  private async acquireSessionToken(scopes: string[], storageKey: string): Promise<string> {
    if (!sessionStorage.getItem('appSessionExpiresAt') || this.isAppSessionExpired()) {
      this.logout(); throw new Error('RMS session expired. Please sign in again.');
    }
    const generation = this.sessionGeneration;
    await this.msalReady;
    if (generation !== this.sessionGeneration || this.isAppSessionExpired()) {
      if (generation === this.sessionGeneration) this.logout();
      throw new Error('RMS session ended.');
    }
    const account = this.msal.instance.getActiveAccount() ?? this.msal.instance.getAllAccounts()[0];
    if (!account) { this.logout(); throw new Error('Microsoft session ended. Please sign in again.'); }
    try {
      const result = await this.msal.instance.acquireTokenSilent({ account, scopes });
      if (generation !== this.sessionGeneration || this.isAppSessionExpired()) throw new Error('RMS session ended.');
      localStorage.setItem(storageKey, result.accessToken);
      return result.accessToken;
    } catch (error: any) {
      if (generation === this.sessionGeneration && (this.isAppSessionExpired() || error instanceof InteractionRequiredAuthError || ['interaction_required','login_required','consent_required','invalid_grant','no_account_error'].includes(error?.errorCode))) this.logout();
      throw error;
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.watcher);
    clearTimeout(this.logoutTimer);
    window.removeEventListener('focus', this.resume);
    document.removeEventListener('visibilitychange', this.resume);
    this.destroy$.next();
    this.destroy$.complete();
  }


  private getGraphClient(accessToken: string) {
    return Client.init({
      authProvider: (done) => {
        done(null, accessToken);
      }
    });
  }

  async getUserDetails(token: string) {
    const client = this.getGraphClient(token);

    // --- User profile ---
    const user = await client.api('/me').get();
    console.log("UserData:", user);

    // --- Photo ---
    // NOTE: Graph JS client sometimes returns ArrayBuffer for $value; handle both Blob and ArrayBuffer
    let photoValue: any;
    try {
      photoValue = await client.api('/me/photo/$value').get();
      const photoBase64 = await this.anyToBase64(photoValue);
      localStorage.setItem('image', photoBase64);
      this.userImageSource.next(photoBase64);
    } catch (e) {
      // Photo can fail if user has no photo or permission not granted
      console.warn('No photo or cannot fetch photo:', e);
    }

    // --- Groups (FIX: page through all results, and keep only groups) ---
    const groups = await this.getAllMyGroups(client);

    // ✅ Store groups in sessionStorage (as requested)
    sessionStorage.setItem('userGroups', JSON.stringify(groups));

    // You can also store other session-based items here if you prefer
    // (leaving your existing localStorage calls intact unless you want to move them too)
      datadogRum.setUser({
          id: user.id || '',
          name: user.displayName || '',
          email: user.mail || user.userPrincipalName || '',
      })
    localStorage.setItem('displayName', user.displayName || '');
    localStorage.setItem('email', user.mail || user.userPrincipalName || '');
    localStorage.setItem('department', user.department || '');
    localStorage.setItem('username', user.userPrincipalName || '');
    localStorage.setItem('title', user.jobTitle || '');
    localStorage.setItem('GUID', user.id || '');

    // Update observables (optional but nice so UI updates immediately)
    this.userNameSource.next(user.displayName || '');
    this.userEmailSource.next(user.mail || user.userPrincipalName || '');
  }
  private async getAllMyGroups(client: any): Promise<any[]> {
    const groups: any[] = [];

    // /me/memberOf returns a collection of directoryObjects (groups, roles, etc.) [1](https://learn.microsoft.com/en-us/answers/questions/706394/getting-access-denied-after-having-the-correct-per)
    // Filter to microsoft.graph.group objects only for safety.
    let requestUrl: string | null = "/me/memberOf?$select=id,displayName,mail,description";

    while (requestUrl) {
      const res: any = await client.api(requestUrl).get();

      const page = (res.value || []).filter((obj: any) => obj['@odata.type'] === '#microsoft.graph.group');

      for (const g of page) {
        groups.push({
          id: g.id,
          displayName: g.displayName,
          mail: g.mail ?? null,
          description: g.description ?? null
        });
      }

      requestUrl = res['@odata.nextLink'] || null;
    }

    return groups;
  }
  private async anyToBase64(value: any): Promise<string> {
    // If it's already a Blob, convert directly
    if (value instanceof Blob) {
      return this.blobToBase64(value);
    }

    // If it's an ArrayBuffer, convert to Blob first
    if (value instanceof ArrayBuffer) {
      const blob = new Blob([value]);
      return this.blobToBase64(blob);
    }

    // If it's a string or something unexpected, just throw
    throw new Error('Unsupported photo response type');
  }
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  completeLogin() {
    this.sessionReady = true;
    if (Number(sessionStorage.getItem('appSessionExpiresAt')) > Date.now() && this.hasValidToken() && localStorage.getItem('ms_authToken')) this.authState.next(true);
  }

  // ✅ Check If User Is Authenticated
  isAuthenticated() {
    return this.authState.asObservable();
  }
  isInAnyGroup(groupNames: string[]): boolean {
    const raw = sessionStorage.getItem('userGroups') || localStorage.getItem('userGroups') || '[]';
    let groups: any[];
    try {
      groups = JSON.parse(raw);
    } catch {
      return false;
    }
    if (!Array.isArray(groups)) return false;
    const names = new Set(groupNames.map(n => n.toLowerCase()));
    return groups.some(g => names.has(String(g?.displayName ?? '').toLowerCase()));
  }

  // 🔓 Logout User
  logout() {
    this.sessionReady = false;
    this.sessionGeneration++;
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('ms_authToken');
    localStorage.removeItem('userGroups');
    localStorage.removeItem('displayName');
    localStorage.removeItem('email');
    localStorage.removeItem('image');
    localStorage.removeItem('department');
    localStorage.removeItem('username');
    localStorage.removeItem('title');
    for (const key of ['forms_authToken','GUID','TraderGUID','token_refreshed']) localStorage.removeItem(key);
    sessionStorage.removeItem('userGroups');
    sessionStorage.removeItem('authIndicator');
    this.userImageSource.next('');
    this.userNameSource.next('');
    this.userEmailSource.next('');
    datadogRum.clearUser();
    
    localStorage.setItem('openTabs', '[{ "title": "Dashboard",  "icon": "dashboard",  "componentKey": "dashboard", "path": "dashboard1", "data": null }]');


    sessionStorage.removeItem('appSessionStartedAt');
    sessionStorage.removeItem('appSessionExpiresAt');


    this.authState.next(false);


    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
    }
    // Since it's a single-screen app, maybe show a modal or update UI state here
    console.warn('🔒 Logged out: 8-hour session limit reached (or token invalid).');
  }

  // 🧪 Helper: Token valid check
  private hasValidToken(): boolean {
    const token = localStorage.getItem('authToken');
    if (!token) return false;

    try {
      const decoded: any = jwtDecode(token);
      return decoded.exp > Date.now().valueOf() / 1000;
    } catch (e) {
      return false;
    }
  }

  startAppSession(): void {
    const now = Date.now();
    const expiresAt = now + this.MAX_SESSION_MS;

    sessionStorage.setItem('appSessionStartedAt', String(now));
    sessionStorage.setItem('appSessionExpiresAt', String(expiresAt));

    this.scheduleHardLogout(); // set a timer as a backup
  }

  isAppSessionExpired(): boolean {
    const expiresAt = Number(sessionStorage.getItem('appSessionExpiresAt') || 0);
    return !!expiresAt && Date.now() >= expiresAt;
  }

  private scheduleHardLogout(): void {
    const expiresAt = Number(sessionStorage.getItem('appSessionExpiresAt') || 0);
    if (!expiresAt) return;

    const delay = Math.max(0, expiresAt - Date.now());

    // reuse your existing timer field
    if (this.logoutTimer) clearTimeout(this.logoutTimer);

    this.logoutTimer = setTimeout(() => {
      this.logout();
    }, delay);
  }
}

